import { QueryKey } from '@tanstack/react-query'
import { SubgraphType } from 'config/subgraph'
import type { SubgraphQueryOptions } from 'hooks/subgraph/queryOptions'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import {
  GetIslandByIdQuery,
  KodiakIslandProtocolQuery,
  useGetIslandByIdQuery,
  useKodiakIslandProtocolQuery as useKodiakIslandProtocolSubgraphQuery,
} from 'queries/islands/generated'
import { useMemo } from 'react'

const islandSubgraphKeyBase = (chainId: number) => ['islands-subgraph', chainId] as const

export const islandSubgraphQueryKeys = {
  island: (chainId: number, id: string) => [...islandSubgraphKeyBase(chainId), 'byId', id] as const,
  protocol: (chainId: number, factoryId: string) => [...islandSubgraphKeyBase(chainId), 'protocol', factoryId] as const,
}

type IslandEntity = GetIslandByIdQuery['kodiakVault']
type ProtocolEntity = KodiakIslandProtocolQuery['kodiakIslandProtocol']

export const useIslandByIdSubgraph = (
  id: string | null | undefined,
  options?: SubgraphQueryOptions<IslandEntity | null, GetIslandByIdQuery>
) => {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const normalizedId = (id ?? '').toLowerCase()
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    refetchInterval = false,
    ...queryOptions
  } = options ?? {}

  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedId)

  return useGetIslandByIdQuery(
    { endpoint },
    { id: normalizedId },
    {
      queryKey: (queryKey ?? islandSubgraphQueryKeys.island(chainId, normalizedId)) as QueryKey,
      enabled,
      retry,
      refetchOnWindowFocus,
      refetchOnReconnect,
      refetchInterval,
      staleTime: 2 * 60 * 1000,
      select: (data) => data?.kodiakVault ?? null,
      ...queryOptions,
    }
  )
}

export const useKodiakIslandProtocol = (
  factoryId: string | null | undefined,
  options?: SubgraphQueryOptions<ProtocolEntity | null, KodiakIslandProtocolQuery>
) => {
  const { chainId } = useConnection()
  const { endpoint } = useSubgraphConfig(SubgraphType.V3)
  const normalizedId = useMemo(() => factoryId?.toLowerCase() ?? '', [factoryId])
  const {
    skip,
    enabled: optionEnabled,
    queryKey,
    retry = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    refetchInterval = false,
    ...queryOptions
  } = options ?? {}

  const enabled = (optionEnabled ?? true) && !skip && Boolean(normalizedId)

  return useKodiakIslandProtocolSubgraphQuery(
    { endpoint },
    { id: normalizedId },
    {
      queryKey: (queryKey ?? islandSubgraphQueryKeys.protocol(chainId, normalizedId)) as QueryKey,
      enabled,
      retry,
      refetchOnWindowFocus,
      refetchOnReconnect,
      refetchInterval,
      staleTime: Infinity,
      select: (data) => data?.kodiakIslandProtocol ?? null,
      ...queryOptions,
    }
  )
}
